import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../../lib/api';
import { useCreatorMe } from '../../lib/auth';
import { SplashLoader } from '../../components/SplashLoader';
import { queryClient } from '../../lib/queryClient';
import logoWhite from '../../assets/logo-white.svg';
import styles from './CreatorLoginPage.module.css';

const ERR_MSG: Record<string, string> = {
  invalid: 'That sign-in link is invalid or has expired. Request a new one below.',
  server: 'Something went wrong. Please try again.',
};

type Tab = 'password' | 'link';

export function CreatorLoginPage() {
  const me = useCreatorMe();
  const [params] = useSearchParams();
  const errKey = params.get('error');
  const [tab, setTab] = useState<Tab>('password');
  const [signedIn, setSignedIn] = useState(false);

  if (me.isPending) return <SplashLoader />;
  if (signedIn || (!me.isError && me.data)) return <Navigate to="/creator" replace />;
  if (me.isError && !(me.error instanceof ApiError && me.error.status === 401)) {
    return <SplashLoader />;
  }

  return (
    <div className={styles.shell}>
      <div className={styles.hero}>
        <div className={styles.heroInner}>
          <img src={logoWhite} alt="Kick" className={styles.logo} />
          <h1 className={styles.heroTitle}>Creator Hub</h1>
          <p className={styles.heroSub}>Access your partnership dashboard</p>
        </div>
      </div>

      <div className={styles.cardWrap}>
        <div className={styles.card}>
          <div className={styles.authTabs}>
            <button
              type="button"
              className={`${styles.authTab} ${tab === 'password' ? styles.authTabActive : ''}`}
              onClick={() => setTab('password')}
            >
              Sign In
            </button>
            <button
              type="button"
              className={`${styles.authTab} ${tab === 'link' ? styles.authTabActive : ''}`}
              onClick={() => setTab('link')}
            >
              Email me a link
            </button>
          </div>
          <div className={styles.formPad}>
            {errKey && <div className={styles.errorBanner}>{ERR_MSG[errKey] ?? errKey}</div>}
            {tab === 'password' ? (
              <PasswordForm onSignedIn={() => setSignedIn(true)} />
            ) : (
              <MagicLinkForm />
            )}
          </div>
        </div>
      </div>
      <footer className={styles.footer}>© 2026 Kick — All rights reserved</footer>
    </div>
  );
}

function PasswordForm({ onSignedIn }: { onSignedIn: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr('');
    if (!email.trim() || !password) {
      setErr('Email and password are required.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/api/creator/login', { email: email.trim(), password });
      await queryClient.invalidateQueries({ queryKey: ['creator', 'me'] });
      onSignedIn();
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : 'Sign-in failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit}>
      {err && <div className={styles.errorBanner}>{err}</div>}
      <label className={styles.label} htmlFor="cr-email">
        Email
      </label>
      <input
        id="cr-email"
        type="email"
        placeholder="you@example.com"
        autoComplete="email"
        className={styles.input}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <label className={styles.label} htmlFor="cr-password">
        Password
      </label>
      <input
        id="cr-password"
        type="password"
        placeholder="••••••••"
        autoComplete="current-password"
        className={styles.input}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <button className={styles.btn} disabled={submitting} type="submit">
        {submitting ? 'Signing in…' : 'Sign In'}
      </button>
      <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 14, lineHeight: 1.5 }}>
        New here? Sign in with a magic link first; you'll be able to set a password from your
        profile page once approved.
      </p>
    </form>
  );
}

function MagicLinkForm() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr('');
    const trimmed = email.trim();
    if (!trimmed) {
      setErr('Email is required.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/api/creator/magic-link', { email: trimmed });
      setSent(true);
    } catch {
      setErr('Something went wrong — please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <div className={styles.successBox}>
        Check your inbox for a sign-in link from Kick. The link expires in 15 minutes.
      </div>
    );
  }

  return (
    <form onSubmit={submit}>
      <div className={styles.infoBox}>
        Enter the email you applied with. We'll send you a one-time sign-in link.
      </div>
      {err && <div className={styles.errorBanner}>{err}</div>}
      <label className={styles.label} htmlFor="ml-email">
        Email
      </label>
      <input
        id="ml-email"
        type="email"
        placeholder="you@example.com"
        autoComplete="email"
        className={styles.input}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <button className={styles.btn} disabled={submitting} type="submit">
        {submitting ? 'Sending…' : 'Send sign-in link'}
      </button>
    </form>
  );
}
